import React, { useState, useEffect } from "react";
import "./../../App.css";
import Select, { components } from "react-select";
import { customStyles } from "./../../helpers/StyleUtils";
import { bookingService } from "../../api/apiService";

function AllFutureBookingGroups() {
  const [bookings, setBookings] = useState([]);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedSet, setSelectedSet] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState({
    label: "new order",
    value: "new order",
  });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [dateRangeOption, setDateRangeOption] = useState("future");
  const [isLoading, setIsLoading] = useState(false);
  const [usedColors, setUsedColors] = useState([]); // Track recently used colors

  const dateRangeOptions = [
    { value: "future", label: "Today and Future" },
    { value: "threeWeeks", label: "Past 3 Weeks and Future" },
    { value: "twoMonths", label: "Past 2 Months and Future" }
  ];

  const colors = [
    // Original colors
    "#FF6633", "#FFB399", "#FF33FF", "#FFFF99", "#00B3E6",
    "#E6B333", "#3366E6", "#999966", "#B34D4D", "#80B300",
    "#809900", "#E6B3B3", "#6680B3", "#66991A", "#FF99E6",
    "#FF1A66", "#E6331A", "#33FFCC", "#ff6600", "#0044cc",
    "#ff9933", "#339933", "#cc0099", "#66cc00", "#ff3366",
    "#0099cc", "#cc3300", "#669900", "#ffcc00", "#0033cc",
    "#cc6600", "#66cc33",
    
    // Additional vibrant colors
    "#FF5733", "#C70039", "#900C3F", "#581845", "#FFC300",
    "#FF5733", "#DAF7A6", "#FFC300", "#FF5733", "#C70039",
    "#8E44AD", "#3498DB", "#E74C3C", "#F39C12", "#2ECC71",
    "#1ABC9C", "#34495E", "#9B59B6", "#E67E22", "#F1C40F",
    "#27AE60", "#16A085", "#2C3E50", "#F39C12", "#D35400",
    "#C0392B", "#8E44AD", "#2980B9", "#17A2B8", "#28A745",
    "#FFC107", "#DC3545", "#6F42C1", "#E83E8C", "#20C997",
    
    // Pastel colors for variety
    "#FFB3BA", "#FFDFBA", "#FFFFBA", "#BAFFC9", "#BAE1FF",
    "#FFCCCB", "#D3D3D3", "#FFE4E1", "#F0E68C", "#E0FFFF",
    "#FAFAD2", "#FFE4B5", "#DCDCDC", "#F5DEB3", "#FFF8DC",
    
    // Darker colors
    "#2F4F4F", "#483D8B", "#8B4513", "#556B2F", "#8B0000",
    "#191970", "#006400", "#8B008B", "#FF8C00", "#9932CC",
    "#8B4513", "#228B22", "#DC143C", "#00CED1", "#FF1493",
    
    // Bright neon-like colors
    "#00FF00", "#FF00FF", "#00FFFF", "#FFFF00", "#FF4500",
    "#ADFF2F", "#FF69B4", "#00FA9A", "#FFD700", "#FF6347",
    "#40E0D0", "#EE82EE", "#90EE90", "#F0E68C", "#DDA0DD",
    
    // Professional colors
    "#4A90E2", "#7ED321", "#F5A623", "#D0021B", "#9013FE",
    "#50E3C2", "#B8E986", "#4A4A4A", "#9B9B9B", "#417505",
    "#BD10E0", "#B4A7D6", "#D5A6BD", "#F78DA7", "#9FACE6",
    
    // Additional vibrant colors
    "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FECA57",
    "#FF9FF3", "#54A0FF", "#5F27CD", "#00D2D3", "#FF9F43",
    "#10AC84", "#EE5A24", "#0984E3", "#A29BFE", "#FD79A8",
    "#FDCB6E", "#6C5CE7", "#74B9FF", "#00B894", "#E17055",
    
    // Earth tones
    "#8D6E63", "#A1887F", "#BCAAA4", "#D7CCC8", "#EFEBE9",
    "#6D4C41", "#5D4037", "#4E342E", "#3E2723", "#795548",
    
    // Ocean/Sky colors
    "#B3E5FC", "#81D4FA", "#4FC3F7", "#29B6F6", "#03A9F4",
    "#0288D1", "#0277BD", "#01579B", "#006064", "#00838F",
    
    // Sunset colors
    "#FFCCBC", "#FFAB91", "#FF8A65", "#FF7043", "#FF5722",
    "#F4511E", "#E64A19", "#D84315", "#BF360C", "#FF3D00",
    
    // Forest/Nature colors
    "#C8E6C9", "#A5D6A7", "#81C784", "#66BB6A", "#4CAF50",
    "#43A047", "#388E3C", "#2E7D32", "#1B5E20", "#2E7D32"
  ];

  // Function to get a non-repeating color for groups based on multiple factors
  const getNonRepeatingColor = (groupNumber = null) => {
    const maxRecentColors = Math.min(15, colors.length - 1); // Track more recent colors for multi-user scenarios
    
    // Get recently used colors from existing groups (last 20 groups by creation time)
    const recentGroups = bookings
      .filter(booking => booking.group && booking.group_color && booking.created_at)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // Sort by creation time, newest first
      .slice(0, 20) // Get last 20 groups
      .map(booking => booking.group_color)
      .filter((color, index, array) => array.indexOf(color) === index); // Remove duplicates
    
    // Combine with our tracked used colors
    const allRecentColors = [...new Set([...usedColors, ...recentGroups])];
    
    // Get available colors (not in recent used list)
    const availableColors = colors.filter(color => !allRecentColors.includes(color));
    
    let selectedColor;
    
    if (availableColors.length > 0) {
      if (groupNumber) {
        // Use group number + current timestamp to create more randomness
        // This prevents multiple users from getting the same color even with similar group numbers
        const seed = groupNumber + Math.floor(Date.now() / 1000); // Add timestamp seed
        const colorIndex = seed % availableColors.length;
        selectedColor = availableColors[colorIndex];
      } else {
        // Pick randomly from available colors
        selectedColor = availableColors[Math.floor(Math.random() * availableColors.length)];
      }
    } else {
      // If all colors are recently used, pick the least recently used color
      const leastRecentColor = colors.find(color => !allRecentColors.slice(0, 60).includes(color));
      if (leastRecentColor) {
        selectedColor = leastRecentColor;
      } else {
        // Last resort: use group number with timestamp to cycle through all colors
        const seed = groupNumber ? groupNumber + Math.floor(Date.now() / 1000) : Math.floor(Math.random() * colors.length);
        const colorIndex = seed % colors.length;
        selectedColor = colors[colorIndex];
      }
    }
    
    // Update used colors list
    setUsedColors(prevUsed => {
      const newUsed = [selectedColor, ...prevUsed.filter(color => color !== selectedColor)];
      // Keep more recent colors for multi-user scenarios
      return newUsed.slice(0, maxRecentColors);
    });
    
    return selectedColor;
  };

  const DropdownIndicator = (props) => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <div style={{ width: "16px", margin: "0" }}>
            <svg
              version="1.1"
              id="Isolationsmodus"
              x="0px"
              y="0px"
              width="16px"
              height="16px"
              viewBox="0 0 322.9 257.78"
              shapeRendering="geometricPrecision"
            >
              <path
                xmlns="http://www.w3.org/2000/svg"
                d="M208.27,7.37c-4.69-4.79-11.12-7.4-17.72-7.81c-0.4-0.03-0.8-0.05-1.22-0.05l0,0h-35.34h-70.2H31.14h-4.27  C17.33-0.46,7.87,4.51,3.34,13.12C-1.19,21.65-1.3,32.3,4.19,40.47c0.85,1.3,1.74,2.56,2.6,3.82C17.3,59.73,27.77,75.2,38.27,90.64  c12.47,18.33,24.91,36.7,37.39,55.03c3.08,4.53,6.13,9.02,9.21,13.54c3.97,5.86,9.06,10.5,16.08,12.36  c6.87,1.86,14.85,0.93,20.9-2.93c3.79-2.45,6.24-5.12,8.8-8.79c8.06-11.54,15.82-23.3,23.76-34.96  c13.14-19.33,26.25-38.67,39.39-57.96c4.68-6.87,9.32-13.73,14-20.63c4.31-6.31,8.39-12.13,8.2-20.41  C215.85,18.95,213.1,12.34,208.27,7.37z"
              />
            </svg>
          </div>
        </components.DropdownIndicator>
      )
    );
  };

  useEffect(() => {
    fetchBookings();
  }, [dateRangeOption]);

  // Initialize used colors from existing groups when bookings are loaded
  useEffect(() => {
    if (bookings.length > 0) {
      const existingGroupColors = bookings
        .filter(booking => booking.group && booking.group_color)
        .map(booking => booking.group_color)
        .filter((color, index, array) => array.indexOf(color) === index) // Remove duplicates
        .slice(-10); // Get the last 10 unique group colors
      
      if (existingGroupColors.length > 0) {
        setUsedColors(existingGroupColors);
        console.log('Initialized used colors from existing groups:', existingGroupColors);
      }
    }
  }, [bookings]);

  const fetchBookings = async () => {
    try {
      setIsLoading(true);
      let startDate = new Date();
      
      if (dateRangeOption === "threeWeeks") {
        startDate.setDate(startDate.getDate() - 21); // 3 weeks ago
      } else if (dateRangeOption === "twoMonths") {
        startDate.setMonth(startDate.getMonth() - 2); // 2 months ago
      }
      
      console.log("Fetching bookings with date:", startDate.toISOString().split('T')[0]);
      const response = await bookingService.getAllBookings(
        1, // page
        3800, // limit - increased to show more bookings
        startDate.toISOString().split('T')[0] // from date
      );
      
      console.log("API Response:", response);
      
      if (response.data && response.data.bookings) {
        const bookingsData = response.data.bookings;
        console.log("Processed bookings data:", bookingsData);
        setBookings(bookingsData);
      } else {
        console.error("API response missing bookings data:", response);
        setBookings([]);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      alert("Error fetching bookings. Please try again.");
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get current bookings without setting loading state
  const getCurrentBookings = async () => {
    try {
      let startDate = new Date();
      
      if (dateRangeOption === "threeWeeks") {
        startDate.setDate(startDate.getDate() - 21);
      } else if (dateRangeOption === "twoMonths") {
        startDate.setMonth(startDate.getMonth() - 2);
      }
      
      const response = await bookingService.getAllBookings(
        1,
        3800,
        startDate.toISOString().split('T')[0]
      );
      
      return response.data && response.data.bookings ? response.data.bookings : [];
    } catch (error) {
      console.error("Error getting current bookings:", error);
      return [];
    }
  };

  const checkForGroupingErrors = (selectedBookingsArray) => {
    let lastClient = null;
    let lastEndTime = new Date(0);

    const sortedBookings = selectedBookingsArray
      .map((bookingId) => bookings.find((b) => b.id === bookingId))
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

    for (const booking of sortedBookings) {
      lastClient = booking.client.label;

      const startTime = new Date(booking.start_date);
      const endTime = new Date(booking.end_date);

      if (startTime < lastEndTime) {
        return "Error: There are overlapping times in the selected bookings.";
      }

      lastEndTime = endTime;
    }

    return null;
  };

  const toggleBookingSelection = (booking) => {
    if (selectedBookings.includes(booking.id)) {
      setSelectedBookings((prev) => prev.filter((id) => id !== booking.id));
    } else {
      setSelectedBookings((prev) => [...prev, booking.id]);
    }
  };

  const groupBookings = async () => {
    const error = checkForGroupingErrors(selectedBookings);
    if (error) {
      alert(error);
      return;
    }

    try {
      setIsLoading(true);
      
      // Refresh bookings first to get the most current data from all users
      await fetchBookings();
      
      // Get the most up-to-date group numbers after refresh
      const currentBookings = bookings.length > 0 ? bookings : await getCurrentBookings();
      const existingGroupNumbers = currentBookings
        .filter(booking => booking.group)
        .map(booking => booking.group);
      const maxGroupNumber = existingGroupNumbers.length > 0 ? Math.max(...existingGroupNumbers) : 0;
      const estimatedGroupNumber = maxGroupNumber + 1;
      
      // Get a color based on the estimated group number
      const groupColor = getNonRepeatingColor(estimatedGroupNumber);
      
      console.log(`Estimated group #${estimatedGroupNumber} assigned color: ${groupColor}, Recent colors: ${usedColors.join(', ')}`);

      // Make a single API call to add all selected bookings to the group
      const response = await bookingService.addToGroup({
        booking_ids: selectedBookings,
        group_color: groupColor
      });

      setSelectedBookings([]);
      await fetchBookings(); // Refresh the bookings list
      // Removed success alert - grouping now happens silently
    } catch (error) {
      console.error("Error grouping bookings:", error);
      alert("Error grouping bookings. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const separateBooking = async (bookingId) => {
    try {
      setIsLoading(true);
      const response = await bookingService.removeFromGroup(bookingId);
      
      if (response.data && response.data.booking) {
        // Update the local bookings state with the updated booking
        setBookings(prevBookings => 
          prevBookings.map(booking => 
            booking.id === bookingId ? response.data.booking : booking
          )
        );
      }
    } catch (error) {
      console.error("Error separating booking:", error);
      alert("Error separating booking. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const separateGroup = async (groupId) => {
    try {
      setIsLoading(true);
      const bookingsToSeparate = bookings.filter(
        (booking) => booking.group === groupId
      );
      
      // Use Promise.all to handle all removals concurrently
      const responses = await Promise.all(
        bookingsToSeparate.map(booking => 
          bookingService.removeFromGroup(booking.id)
        )
      );
      
      // Update the local bookings state with all updated bookings
      setBookings(prevBookings => {
        const updatedBookings = [...prevBookings];
        responses.forEach(response => {
          if (response.data && response.data.booking) {
            const index = updatedBookings.findIndex(b => b.id === response.data.booking.id);
            if (index !== -1) {
              updatedBookings[index] = response.data.booking;
            }
          }
        });
        return updatedBookings;
      });
      
    } catch (error) {
      console.error("Error separating group:", error);
      alert("Error separating group. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const clientOptions = [
    ...new Set(bookings
      .filter(booking => booking.client && booking.client.label)
      .map((booking) => booking.client.label)
    ),
  ].map((client) => ({ value: client, label: client }));
  
  const positionOptions = [
    ...new Set(bookings
      .filter(booking => booking.position && booking.position.label)
      .map((booking) => booking.position.label)
    ),
  ].map((position) => ({ value: position, label: position }));
  
  const setOption = [
    ...new Set(bookings
      .filter(booking => booking.set)
      .map((booking) => booking.set)
    )
  ].map((set) => ({ value: set, label: set }));
  
  const locationOptions = [
    ...new Set(bookings.map((booking) => {
      // Extract location string from object or return the string directly
      if (booking.location && typeof booking.location === 'object') {
        return booking.location.short || booking.location.name || booking.location.city || '';
      } else if (typeof booking.location === 'string') {
        return booking.location;
      }
      return '';
    }).filter(location => location !== '')), // Remove empty strings
  ].map((location) => ({ value: location, label: location }));
  
  const statusOption = [
    ...new Set(bookings
      .filter(booking => booking.status)
      .map((booking) => booking.status)
    ),
  ].map((status) => ({ value: status, label: status }));

  const filteredEvents = bookings.filter((event) => {
    const matchesClient =
      !selectedClient || (event.client && event.client.label === selectedClient.value);
    const matchesPosition =
      !selectedPosition || (event.position && event.position.label === selectedPosition.label);
    const matchesSet = !selectedSet || event.set === selectedSet.value;
    const matchesLocation = !selectedLocation || (() => {
      // Extract location string from the event's location field
      let eventLocationString = '';
      if (event.location && typeof event.location === 'object') {
        eventLocationString = event.location.short || event.location.name || event.location.city || '';
      } else if (typeof event.location === 'string') {
        eventLocationString = event.location;
      }
      return eventLocationString === selectedLocation.value;
    })();
    const matchesStatus =
      !selectedStatus || event.status === selectedStatus.value;

    return matchesClient && matchesPosition && matchesSet && matchesLocation && matchesStatus;
  });

   // Helper function to get talent name from booking
   const getTalentName = (booking) => {
    if (!booking) return '';
    
    // Check different possible property names for talent name
    if (booking.talent_name) {
      return booking.talent_name;
    }
    
    if (booking.talent && typeof booking.talent === 'object') {
      return booking.talent.name || booking.talent.Name || booking.talent.label || '';
    } else if (booking.talent && typeof booking.talent === 'string') {
      return booking.talent;
    }
    
    if (booking.talentName) {
      return booking.talentName;
    }
    
    if (booking.Talent) {
      if (typeof booking.Talent === 'object') {
        return booking.Talent.name || booking.Talent.Name || booking.Talent.label || '';
      }
      return booking.Talent;
    }
    
    return '';
  };
  // Helper function for SonstigeInfo field
  const getSonstigeInfoDisplay = (booking) => {
    if (!booking) return '';
    
    // Check different possible property names for additional info
    if (booking.sonstige_info) {
      if (typeof booking.sonstige_info === 'object') {
        return booking.sonstige_info.text || booking.sonstige_info.value || '';
      }
      return booking.sonstige_info;
    }
    
    if (booking.SonstigeInfo) {
      if (typeof booking.SonstigeInfo === 'object') {
        return booking.SonstigeInfo.text || booking.SonstigeInfo.value || '';
      }
      return booking.SonstigeInfo;
    }
    
    if (booking.additional_info) {
      if (typeof booking.additional_info === 'object') {
        return booking.additional_info.text || booking.additional_info.value || '';
      }
      return booking.additional_info;
    }
    
    return '';
  };

  // Helper function for Location field
  const getLocationDisplay = (booking) => {
    if (!booking || !booking.location) return '';
    
    if (typeof booking.location === 'object') {
      return booking.location.short || booking.location.name || booking.location.city || '';
    } else if (typeof booking.location === 'string') {
      return booking.location;
    }
    
    return '';
  };

  // Helper function for Studio field
  const getStudioDisplay = (booking) => {
    if (!booking || !booking.studio) return '';
    
    if (typeof booking.studio === 'object') {
      return booking.studio.name || '';
    } else if (typeof booking.studio === 'string') {
      return booking.studio;
    }
    
    return '';
  };

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Orders to Group</h1>
        <div className="future-booking">
          <div style={{ marginTop: "0px", marginBottom: "60px", display: "flex" }}>
            <div style={{ marginRight: "10px" }}>
              <Select
                value={dateRangeOptions.find(option => option.value === dateRangeOption)}
                onChange={(selected) => setDateRangeOption(selected.value)}
                options={dateRangeOptions}
                isClearable={false}
                placeholder="Select Date Range"
                components={{ DropdownIndicator }}
                styles={customStyles}
              />
            </div>
            <div style={{ marginRight: "10px" }}>
              <Select
                value={selectedClient}
                onChange={setSelectedClient}
                options={clientOptions}
                isClearable
                placeholder="Filter by Client"
                components={{ DropdownIndicator }}
                styles={customStyles}
              />
            </div>
            <Select
              value={selectedPosition}
              onChange={setSelectedPosition}
              options={positionOptions}
              isClearable
              placeholder="Filter by Function"
              components={{ DropdownIndicator }}
              styles={customStyles}
            />
            <Select
              value={selectedSet}
              onChange={setSelectedSet}
              options={setOption}
              isClearable
              placeholder="Filter by Set"
              components={{ DropdownIndicator }}
              styles={customStyles}
            />
            <Select
              value={selectedLocation}
              onChange={setSelectedLocation}
              options={locationOptions}
              isClearable
              placeholder="Filter by Location"
              components={{ DropdownIndicator }}
              styles={customStyles}
            />
            <Select
              value={selectedStatus}
              onChange={setSelectedStatus}
              options={statusOption}
              isClearable
              placeholder="Filter by Status"
              components={{ DropdownIndicator }}
              styles={customStyles}
            />
          </div>

          {isLoading ? (
            <div className="spinner"></div>
          ) : filteredEvents.length === 0 ? (
            <div className="no-data-message">
              {bookings.length === 0 ? "No bookings found" : "No bookings match the selected filters"}
            </div>
          ) : (
            <div className="content-wrapper content-wrapper-talent-list">
              <div className="list-tasks-header">
                <div style={{ flexBasis: "30px" }}></div>
                <div style={{ flexBasis: "40px", fontSize: "11px" }}>Group</div>
                <div style={{ flexBasis: "170px" }}>Client</div>
                <div style={{ flexBasis: "180px" }}>Date & Time</div>
                <div style={{ flexBasis: "120px" }}>Position</div>
                <div style={{ flexBasis: "100px" }}>Set</div>
                <div style={{ flexBasis: "160px" }}>Location/Studio</div>
                <div style={{ flexBasis: "150px" }}>Status</div>
                <div style={{ flexBasis: "120px" }}>S.Info</div>
                <div style={{ flexBasis: "120px" }}>Talent</div>
                <div style={{ flexBasis: "200px" }}>Actions</div>
                
              </div>

              {filteredEvents.map((booking, index) => {
                const isGrouped = booking.group;
                const groupSize = bookings.filter(
                  (b) => b.group === booking.group
                ).length;

                const isFirstInGroup =
                  isGrouped &&
                  (index === 0 || bookings[index - 1].group !== booking.group);
                const isLastInGroup =
                  isGrouped &&
                  (index === bookings.length - 1 ||
                    bookings[index + 1].group !== booking.group);

                return (
                  <div
                    key={booking.id}
                    className={[
                      "list-tasks-item",
                      isGrouped ? "groupedBooking" : "singleBooking",
                      isFirstInGroup ? "firstInGroup" : "",
                      isLastInGroup ? "lastInGroup" : "",
                    ].join(" ")}
                    style={isGrouped ? { borderColor: booking.group_color } : {}}
                  >
                    <div className="custom-checkbox" style={{ flexBasis: "30px" }}>
                      <input
                        type="checkbox"
                        id={`checkbox-${index}`}
                        checked={selectedBookings.includes(booking.id)}
                        onChange={() => toggleBookingSelection(booking)}
                      />
                      <label htmlFor={`checkbox-${index}`}></label>
                    </div>
                    <div style={{ flexBasis: "40px", fontSize: "11px", color: "#666", fontWeight: "bold" }}>
                      {isGrouped ? `#${booking.group}` : ""}
                    </div>
                    <div style={{ flexBasis: "170px" }}>
                      {booking.client?.label || "N/A"}
                    </div>
                    <div style={{ flexBasis: "180px" }}>
                      {new Date(booking.start_date).toLocaleDateString()} {booking.start_time} - {booking.end_time}
                    </div>
                    <div style={{ flexBasis: "120px" }}>
                      {booking.position?.label || "N/A"}
                    </div>
                    <div style={{ flexBasis: "100px" }}>
                      {booking.set || "N/A"}
                    </div>
                    <div style={{ flexBasis: "160px" }}>
                      {(() => {
                        const location = getLocationDisplay(booking);
                        const studio = getStudioDisplay(booking);
                        
                        if (location && studio) {
                          return `${location} / ${studio}`;
                        } else if (location) {
                          return location;
                        } else if (studio) {
                          return studio;
                        } else {
                          return "N/A";
                        }
                      })()}
                    </div>
                    <div style={{ flexBasis: "150px" }}>
                      {booking.status || "N/A"}
                    </div>
                    <div style={{ flexBasis: "120px" }}>
                      {getSonstigeInfoDisplay(booking) || "N/A"}
                    </div>
                    <div style={{ flexBasis: "120px" }}>
                      {getTalentName(booking) || "N/A"}
                    </div>
                    <div style={{ flexBasis: "200px" }}>
                      {isGrouped && (
                        groupSize > 2 ? (
                          <button
                            className="button"
                            onClick={() => separateBooking(booking.id)}
                          >
                            Separate
                          </button>
                        ) : (
                          isFirstInGroup && (
                            <button
                              className="button"
                              onClick={() => separateGroup(booking.group)}
                            >
                              Separate
                            </button>
                          )
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <button
          style={{ marginTop: "30px" }}
          className="big-button"
          onClick={groupBookings}
          disabled={selectedBookings.length === 0 || isLoading}
        >
          {isLoading ? "Processing..." : "Create/Edit Group"}
        </button>
      </div>
    </div>
  );
}

export default AllFutureBookingGroups;
